import type { User, UserRole } from '../types/api';

/**
 * Ensures user has role_info populated, even if only role field exists
 * This handles backward compatibility and consistency issues
 */
export function normalizeUserRole(user: User | null): User | null {
  if (!user) return null;
  
  // If role_info is already properly set, return as-is
  if (user.role_info?.value) {
    return user;
  }
  
  // If only role field exists, create role_info
  if (user.role) {
    const roleLabels: Record<UserRole, string> = {
      super_admin: 'Super Admin',
      admin: 'Admin',
      teacher: 'Teacher',
      operator: 'Operator',
      viewer: 'Viewer',
      parent: 'Parent'
    };
    
    return {
      ...user,
      role_info: {
        value: user.role,
        label: roleLabels[user.role] || user.role
      }
    };
  }
  
  // No role information available
  // Warning: User has no role information
  return user;
}

/**
 * Gets the user's role value from either role_info or role field
 */
export function getUserRole(user: User | null): UserRole | null {
  if (!user) return null;
  return user.role_info?.value || user.role || null;
}

/**
 * Gets the user's role label for display
 */
export function getUserRoleLabel(user: User | null): string {
  if (!user) return '';
  return user.role_info?.label || user.role || '';
}